import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import {
    createUseStyles,
    Input,
    Textarea,
    Button,
    Advice,
    SectionForm,
    Checkbox,
    Text,
    Spacer,
    useTheme,
} from '@Sage/hoi-poi-ui';
import { getTools } from 'lib/services/copilotAgents';
import { useWeb5Context } from 'lib/Web5Context';
import CopilotSidebarTabHeader from '../../components/CopilotSidebarTabHeader';
import { CopilotContext } from '../../../Context';

import styles from './styles';
const useStyles = createUseStyles(styles, 'CopilotSidebarAgentsForm');

const CopilotSidebarAgentsForm = ({ agent, onCancel, onSave }) => {
    const { t } = useWeb5Context();
    const theme = useTheme();
    const classes = useStyles();
    const { refreshAgents } = useContext(CopilotContext);
    const [isLoading, setIsLoading] = useState(false);
    const [form, setForm] = useState(
        agent || {
            name: '',
            description: '',
            tools: [],
        },
    );
    const [tools, setTools] = useState();

    const selectedTools = useMemo(() => {
        return form?.tools?.map((tool) => tool.name) || [];
    }, [form?.tools]);

    const onChange = useCallback(
        (key) => (value) => {
            setForm({ ...form, [key]: value });
        },
        [form],
    );

    const onInnerCancel = useCallback(() => {
        onCancel();
        setTimeout(() => {
            setForm({
                name: '',
                description: '',
            });
        });
    }, [onCancel]);

    const onInnerSave = useCallback(() => {
        setIsLoading(true);
        onSave(form)
            .then(() => {
                onInnerCancel();
            })
            .finally(() => {
                setIsLoading(false);
                refreshAgents();
            });
    }, [form, onInnerCancel, onSave, refreshAgents]);

    const onChangeTool = useCallback(
        (name) => {
            if (selectedTools.includes(name)) {
                setForm({ ...form, tools: form.tools.filter((tool) => tool.name !== name) });
            } else {
                setForm({
                    ...form,
                    tools: [...form.tools, tools.find((tool) => tool.name === name)],
                });
            }
        },
        [form, selectedTools, tools],
    );

    useEffect(() => {
        getTools()
            .then(({ data }) => {
                setTools(data);
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    const toolsOptions = useMemo(() => {
        return tools?.map((tool) => (
            <div className={classes.tool} key={tool.name}>
                <Checkbox
                    className={classes.toolCheckbox}
                    checked={selectedTools.includes(tool.name)}
                    onChange={() => onChangeTool(tool.name)}
                    color="actionMinor"
                />
                <div className={classes.toolContent}>
                    <Text bold>{tool.label || tool.name}</Text>
                    <Text type="caption" color={theme.colors.textLight.secondary}>
                        {tool.description}
                    </Text>
                </div>
            </div>
        ));
    }, [
        classes.tool,
        classes.toolCheckbox,
        classes.toolContent,
        onChangeTool,
        selectedTools,
        theme.colors.textLight.secondary,
        tools,
    ]);

    return (
        <div className={classes.root}>
            <CopilotSidebarTabHeader
                title={t(agent?.id ? 'label_copilot_edit_agent' : 'label_copilot_create_agent')}
                onBack={onInnerCancel}
                sideComponent={
                    <Button
                        type="primary"
                        onClick={onInnerSave}
                        isLoading={isLoading}
                        isDisabled={!form.name || !form.description || !form.instructions}
                    >
                        {t('action_save')}
                    </Button>
                }
            />
            <Advice
                type="semanticInfo"
                title={t('label_copilot_agent_advice_title')}
                showIcon
                defaultCollapsed={false}
            >
                {t('label_copilot_agent_advice_role')} <br />
                {t('label_copilot_agent_advice_tone')} <br />
                {t('label_copilot_agent_advice_context')} <br />
                {t('label_copilot_agent_advice_limits')}
            </Advice>
            <SectionForm title={t('label_copilot_agent_section_info')}>
                <Input
                    label={t('label_name')}
                    placeholder={t('placeholder_copilot_agent_name')}
                    value={form.name}
                    onChange={onChange('name')}
                    isFullWidth
                    isRequired
                />
                <Textarea
                    label={t('label_description')}
                    placeholder={t('placeholder_copilot_agent_description')}
                    value={form.description}
                    onChange={onChange('description')}
                    minRows={2}
                    maxRows={4}
                    isResizable={false}
                    isFullWidth
                    isRequired
                />
                <Textarea
                    className={classes.instructions}
                    label={t('label_copilot_agent_instructions')}
                    placeholder={t('placeholder_copilot_agent_instructions')}
                    value={form.instructions}
                    onChange={onChange('instructions')}
                    minRows={10}
                    maxRows={40}
                    isFullWidth
                    isRequired
                />
            </SectionForm>
            {tools?.length > 0 && (
                <SectionForm title={t('label_copilot_agent_section_tools')}>
                    <Spacer y={3} />
                    <div className={classes.tools}>{toolsOptions}</div>
                </SectionForm>
            )}
        </div>
    );
};

export default CopilotSidebarAgentsForm;
